import { useCallback, useRef, useState } from 'react'
import type { SubmissionErrorCode, SubmissionResult } from '@assessments/shared'
import { isSessionExpired } from '../../services/apiError.js'
import { SubmissionRefusedError, submitAssessment } from './api.js'

export type SubmitStatus = 'idle' | 'submitting' | 'submitted' | 'error'

export interface UseSubmitAssessment {
  status: SubmitStatus
  result: SubmissionResult | null
  /** The server's refusal reason; null for a transport failure or before any attempt. */
  errorCode: SubmissionErrorCode | null
  submit: () => Promise<void>
}

/**
 * Runs one submission at a time. `onSettled` fires after any server answer (success or
 * refusal) so the caller can refetch the overview and show the authoritative state.
 */
export function useSubmitAssessment(
  organizationSurveyId: string,
  { onSettled, onSessionExpired }: { onSettled?: () => void; onSessionExpired?: () => void } = {},
): UseSubmitAssessment {
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [result, setResult] = useState<SubmissionResult | null>(null)
  const [errorCode, setErrorCode] = useState<SubmissionErrorCode | null>(null)
  const inFlight = useRef(false)

  const submit = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setStatus('submitting')
    setErrorCode(null)
    try {
      setResult(await submitAssessment(organizationSurveyId))
      setStatus('submitted')
      onSettled?.()
    } catch (err) {
      if (isSessionExpired(err)) {
        onSessionExpired?.()
      } else if (err instanceof SubmissionRefusedError) {
        setErrorCode(err.code)
        onSettled?.()
      }
      setStatus('error')
    } finally {
      inFlight.current = false
    }
  }, [organizationSurveyId, onSettled, onSessionExpired])

  return { status, result, errorCode, submit }
}
